import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { push, replace } from 'connected-react-router';
import { Routes } from 'routes';

import { auth, storage } from 'lib/api/firebase/firebase-api';

// only used for typehinting
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';

import { updateProfile } from 'firebase/auth';

import { APIAuth } from 'lib/api/firebase/apis/api-auth';
import { AppThunk } from 'lib/store/store';
import { handleError, IHandleError } from 'lib/utils/redux';
import { Role } from './types';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';

/**
 * A reference to the Firestore Storage API
 *
 * We currently only use this for managing profile photos
 *
 * @deprecated
 */
const storageRef = storage.ref();

// TODO: Delete after QA passes for the next release
// const temporaryValues = {
//   refreshedTokenAfterMissingClaims: false
// };

export interface User {
  displayName: string | null;
  email: string | null;
  phoneNumber: string | null;
  uid: string;
  role: Role;
  extraRole: Role | null;
  photoURL?: string | null;
  needsPassword: boolean;
  isPartial: boolean;
}

export interface AuthState extends IHandleError {
  isUserDataLoading: boolean;
  isAuthActionActive: boolean;
  user: User | null;
  error: Error | null;
}

export const initialState: AuthState = {
  isUserDataLoading: true,
  isAuthActionActive: false,
  user: null,
  hasError: false,
  isLoading: false,
  error: null
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    signIn(state, action: PayloadAction<User>) {
      state.isUserDataLoading = false;
      state.isLoading = false;
      state.error = null;
      state.user = action.payload;
    },

    signOut(state) {
      state.isUserDataLoading = false;
      state.user = null;
      state.error = null;
    },

    /**
     * Set a flag that something in the auth flow is loading
     */
    startLoading(state) {
      state.isUserDataLoading = true;
    },

    stopLoading(state) {
      state.isUserDataLoading = false;
    },

    startLoadingAction(state) {
      state.isAuthActionActive = true;
    },
    stopLoadingAction(state) {
      state.isAuthActionActive = false;
    },
    setError(state, action: PayloadAction<string>) {
      state.isUserDataLoading = false;
      state.isAuthActionActive = false;
      handleError(
        state,
        { error: new Error(action.payload) },
        'Authentication'
      );
    },
    resetError(state) {
      state.error = null;
    },
    setUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      // state.isLoading = false;
    }
  }
});

export const { signIn, setError, startLoading, stopLoading } =
  authSlice.actions;
export const AuthSliceActions = authSlice.actions;
export const AuthSliceReducer = authSlice.reducer;
export default authSlice.reducer;

export const sendPasswordReset =
  (email: string): AppThunk =>
  async dispatch => {
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        dispatch(push('/'));
      })
      .catch(error => {
        console.log(
          'An error occurred while sending a passwordResetEmail',
          error
        );
      });
  };

/**
 * Action to upload a profile image
 *
 * @deprecated
 *
 * @param file
 * @returns
 */
export const uploadProfileImage =
  (file: File): AppThunk =>
  async () => {
    // dispatch, getState
    if (file === null) return; // no file uploaded

    // get current user auth
    const currentUser = auth.currentUser;
    if (currentUser === null) return; // no current user logged in

    // filename scheme shoudl just be profile_image_uid in the profile_images directory and that's it we keep it simple
    const fileNameScheme = `profile_image_${currentUser.uid}.jpg`;
    const uploadPath = storageRef.child('profile_images/' + fileNameScheme);

    uploadPath
      .put(file)
      .then(snapshot => {
        // // update auth object to save fullpath of snapshot
        snapshot.ref.getDownloadURL().then(url => {
          // console.log("url: ", url);
          updateProfile(currentUser, {
            photoURL: url
          })
            .then(() => {
              // need to reload user to get changes in photoURL to propagate after setting
              currentUser.reload();
            })
            .catch(error => {
              console.log('updateProfile errored: ', error);
            });
        });
      })
      .catch(error => {
        console.log('upload error: ', error);
      });
  };

export const restoreAuth =
  (auth: firebase.auth.Auth): AppThunk =>
  async (dispatch, getState) => {
    /**
     * triggers on token id change, signin, signout(?)
     */
    async function onIdTokenChanged(firebaseUser: firebase.User | null) {
      try {
        dispatch(authSlice.actions.startLoading());
        if (firebaseUser === null) {
          dispatch(authSlice.actions.stopLoading());
          return;
        }

        // Get the ID token
        await firebaseUser.getIdTokenResult();
        /**
         * 2026-04-08
         * Disabled this section because it is not used.
         * TODO: Delete after QA passes.
         */
        // /**
        //  * This can happen if:
        //  * 1. It is a new user and the initUser API failed or is still executing
        //  * 2. It is an existing user and the claims are missing or removed
        //  */
        // if (!token.claims.role) {
        //   console.warn(
        //     `Token claims for user ${firebaseUser.uid} are missing. This can be caused by a network delay`,
        //     token.claims
        //   );

        //   /**
        //    * Fetch the latest ID token once
        //    */
        //   if (!temporaryValues.refreshedTokenAfterMissingClaims) {
        //     await auth.currentUser?.getIdToken(true);
        //     temporaryValues.refreshedTokenAfterMissingClaims = true;
        //   }

        //   return;
        // } else {
        //   temporaryValues.refreshedTokenAfterMissingClaims = false;
        // }
        let me;
        try {
          me = await APIAuth.getMe().then(_ => _.data);
        } catch (e) {
          console.error(resolveError(e));
          throw new Error(
            `Failed to fetch authentication session data. Please report this error to ConcussionRx.`
          );
        }

        const user: User = {
          displayName: me.displayName,
          email: me.email,
          phoneNumber: me.phoneNumber ?? null,
          uid: me.uid,
          role: me.role as Role,
          extraRole: me.extraRole as Role | null,
          photoURL: me.photoURL,
          needsPassword: me.needsPassword ?? false,
          isPartial: me.isPartial
        };

        // Save the user data into the app state
        dispatch(authSlice.actions.signIn(user));

        /**
         * check if email is verified for user
         * timer to refresh user every two seconds to listen for emailVerification
         */
        // const emailVerified = firebaseUser.emailVerified;

        // // If the user is not verified
        // if (emailVerified === false) {
        //   dispatch(goToVerifyEmail());
        //   return;
        // }
      } catch (error) {
        dispatch(authSlice.actions.setError((error as Error).message));
      } finally {
        dispatch(authSlice.actions.stopLoading());
      }
    }

    auth.onIdTokenChanged(onIdTokenChanged);
    auth.onAuthStateChanged(user => {
      // The user isn't signed in or something failed to load
      if (!user) {
        dispatch(authSlice.actions.stopLoading());
        return;
      }

      // TODO: test where we can take into onAuthStateChanged here;
      const currentPath = getState().router.location.pathname;

      /**
       * Redirect the user if the user has verified their email and if they are on the VerifyEmail page
       * TODO: Redirection logic should happen in the router
       */
      if (user && user.emailVerified && currentPath === Routes.VerifyEmail) {
        dispatch(push('/'));
      }
    });
  };

export const goToVerifyEmail = (): AppThunk => async dispatch => {
  dispatch(replace(Routes.VerifyEmail));
};
